import { c as _c } from "react/compiler-runtime";
import { MultiSelect } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { useBuilder } from '../../store/provider';
import type { DecisionNode, DecisionStateRef } from '../../store/types';
import { isDecisionNode, isManagedNode, isRulesNode, isTaskNode } from '../../store/types';
import { uid } from '../../store/utils';
type StateRefsProps = {
  node: DecisionNode;
};
type RefChoice = Pick<DecisionStateRef, 'source' | 'key'> & {
  isImage?: boolean;
};
const refToken = ({
  source,
  key
}: RefChoice): string => `${source}s.${key}`;
const StateRefs = t0 => {
  const $ = _c(38);
  const {
    node
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.decision.state"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const {
    nodes,
    inputs
  } = state.bundle;
  let choices;
  let t2;
  if ($[1] !== inputs || $[2] !== node.id || $[3] !== nodes || $[4] !== t) {
    let t3;
    if ($[7] !== node.id) {
      t3 = candidate => candidate.id === node.id;
      $[7] = node.id;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    const earlier = nodes.slice(0, nodes.findIndex(t3));
    choices = [...inputs.filter(_temp).map(_temp2), ...earlier.flatMap(_temp7)];
    t2 = choices.map(choice => ({
      value: refToken(choice),
      label: choice.isImage ? t(_temp8, {
        token: refToken(choice)
      }) : refToken(choice)
    }));
    $[1] = inputs;
    $[2] = node.id;
    $[3] = nodes;
    $[4] = t;
    $[5] = choices;
    $[6] = t2;
  } else {
    choices = $[5];
    t2 = $[6];
  }
  const options = t2;
  let t3;
  if ($[9] !== actions.decision || $[10] !== choices || $[11] !== node.id || $[12] !== node.state) {
    t3 = tokens => {
      const refs = tokens.flatMap(token => {
        const kept = node.state.find(ref => refToken(ref) === token);
        if (kept) {
          return [kept];
        }
        const choice_0 = choices.find(candidate_1 => refToken(candidate_1) === token);
        return choice_0 ? [{
          id: uid("sref"),
          source: choice_0.source,
          key: choice_0.key
        }] : [];
      });
      actions.decision.setStateRefs(node.id, refs);
    };
    $[9] = actions.decision;
    $[10] = choices;
    $[11] = node.id;
    $[12] = node.state;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleChange = t3;
  let t4;
  if ($[14] !== t) {
    t4 = t(_temp9);
    $[14] = t;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <span className="shrink-0 text-body-3 text-tertiary">{t4}</span>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== node.state) {
    t6 = node.state.map(refToken);
    $[18] = node.state;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t) {
    t7 = t(_temp10);
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== handleChange || $[23] !== isReadOnly || $[24] !== options || $[25] !== t6 || $[26] !== t7) {
    t8 = <MultiSelect value={t6} options={options} placeholder={t7} hideSelectAll={true} disabled={isReadOnly} onChange={handleChange} />;
    $[22] = handleChange;
    $[23] = isReadOnly;
    $[24] = options;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t5 || $[29] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}{t8}</div>;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t) {
    t10 = t(_temp11);
    $[31] = t;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10) {
    t11 = <span className="text-caption-3 text-tertiary">{t10}</span>;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11 || $[36] !== t9) {
    t12 = <div className="flex flex-col gap-1.5 rounded border border-tertiary bg-secondary p-2">{t9}{t11}</div>;
    $[35] = t11;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
export default StateRefs;
function _temp(input) {
  return input.name.trim();
}
function _temp2(input_0) {
  return {
    source: "input" as const,
    key: input_0.name,
    isImage: input_0.type === "document"
  };
}
function _temp3(field) {
  return field.name.trim();
}
function _temp4(field_0) {
  return {
    source: "output",
    key: field_0.name
  };
}
function _temp5(q) {
  return q.key.trim();
}
function _temp6(q_0) {
  return {
    source: "output",
    key: q_0.key
  };
}
function _temp7(candidate_0) {
  const task = isManagedNode(candidate_0) ? candidate_0.task : isTaskNode(candidate_0) ? candidate_0 : null;
  if (task) {
    return task.fields.filter(_temp3).map(_temp4);
  }
  if (isRulesNode(candidate_0)) {
    return candidate_0.key.trim() ? [{
      source: "output",
      key: candidate_0.key
    }] : [];
  }
  if (isDecisionNode(candidate_0)) {
    return candidate_0.questions.filter(_temp5).map(_temp6);
  }
  return [];
}
function _temp8($) {
  return $.image;
}
function _temp9($_0) {
  return $_0.label;
}
function _temp10($_1) {
  return $_1.everything;
}
function _temp11($_2) {
  return $_2.hint;
}